import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import SimpleBar from "simplebar-react";
import AdminTable from "../table";
import { formatNumber } from "../../utils/functions";
import { useTranslation } from "react-i18next";
import SecondaryButton from "canvas-core-react/lib/SecondaryButton";
import ErrorMessage from "../custom-components/errorMessage";

const MAX_CHOICE_NUMBER = 2;
const CREDIT_TYPES = ["reverse", "overdraft"];

const FacilitiesComponent = ({
  facilities,
  selectedFacilities,
  handleFacilities,
  closeModal,
}) => {
  const { t } = useTranslation();
  const [subfacilities, setSubfacilities] = useState({});
  const [selectionError, setSelectionError] = useState(false);
  const [currencies, setCurrencies] = useState([]);

  useEffect(() => {
    const radioButtons = {};

    const availableCurrencies = [];
    facilities.forEach((facility) => {
      /* istanbul ignore else */
      if (!availableCurrencies.includes(facility.currencyCode)) {
        availableCurrencies.push(facility.currencyCode);
      }
    });

    setCurrencies(availableCurrencies);

    availableCurrencies.forEach((currency) => {
      radioButtons[currency] = {};
      facilities
        .filter((facilities) => facilities.currencyCode === currency)
        .forEach((facility) => {
          const lineOfCredit = facility.lineOfCreditNumber;
          const selected = selectedFacilities.find(
            (selectedFacility) =>
              selectedFacility.lineOfCreditNumber === lineOfCredit
          );

          radioButtons[currency][lineOfCredit] = {};
          radioButtons[currency][lineOfCredit]["reverse"] = selected
            ? facility.isReverseLine
            : false;

          radioButtons[currency][lineOfCredit]["overdraft"] = selected
            ? facility.isOverdraftLine
            : false;

          radioButtons[currency][lineOfCredit]["value"] = lineOfCredit;
        });
    });

    setSubfacilities(radioButtons);
  }, []);

  const handleSubfacilitySelection = (event, lineOfCredit, currency, type) => {
    const linesOfCredit = Object.keys(subfacilities[currency]);
    const oppositeTypeOfCredit = type === "reverse" ? "overdraft" : "reverse";

    subfacilities[currency][lineOfCredit][type] = true;
    subfacilities[currency][lineOfCredit][oppositeTypeOfCredit] = false;

    let creditCounter = 0;
    let creditAffected = "";
    linesOfCredit.forEach((credit) => {
      /* istanbul ignore else */
      if (
        subfacilities[currency][credit][type] ||
        subfacilities[currency][credit][oppositeTypeOfCredit]
      ) {
        creditCounter++;
      }
      /* istanbul ignore else */
      if (subfacilities[currency][credit][type] && credit !== lineOfCredit) {
        creditAffected = credit;
        subfacilities[currency][credit][type] = false;
        subfacilities[currency][credit][oppositeTypeOfCredit] = true;
      }
    });

    /* the credit counter validates the maximum quantity of
      choices you can select for each currency if the quantity
      of columns is greater than 2 per currency you should match
      this number to current available column number
    */
    /* istanbul ignore else */
    if (creditCounter > MAX_CHOICE_NUMBER) {
      subfacilities[currency][creditAffected][type] = false;
      subfacilities[currency][creditAffected][oppositeTypeOfCredit] = false;
    }

    setSubfacilities({ ...subfacilities });
  };

  const getTableColumns = () => {
    let base = `clients.profile.company.facilities.table`;
    const tableHeaders = [
      { selector: "lineOfCreditNumber", name: t(`${base}.lineOfCreditNumber`) },
      { selector: "currency", name: t(`${base}.currency`) },
      { selector: "subProductCode", name: t(`${base}.subProductCode`) },
      // { selector: "expirationDate", name: t(`${base}.expirationDate`) },
      {
        selector: "formatApprovedAmount",
        name: t(`${base}.formatApprovedAmount`),
      },
      // { selector: "formatRemainder", name: t(`${base}.formatRemainder`) },
    ];

    currencies.forEach((currency) => {
      tableHeaders.push({
        selector: `${currency.toLowerCase()}ReverseSubfacility`,
        name: t(`${base}.${currency.toLowerCase()}ReverseSubfacility`),
        cellFormatter: (row) => facilityRadioGroup(row, currency, "reverse"),
      });

      tableHeaders.push({
        selector: `${currency.toLowerCase()}OverdraftSubfacility`,
        name: t(`${base}.${currency.toLowerCase()}OverdraftSubfacility`),
        cellFormatter: (row) => facilityRadioGroup(row, currency, "overdraft"),
      });
    });
    return tableHeaders;
  };

  const facilityRadioGroup = (facility, currency, type) => {
    const lineOfCredit = facility.lineOfCreditNumber;
    let radioChecked = false;

    if (facility.currency === currency) {
      radioChecked = subfacilities[currency][lineOfCredit][type];
    }

    return (
      <div className="custom-radio">
        <input
          id={`${currency}_${type}_${lineOfCredit}`}
          type="radio"
          className="custom-radio-input"
          name={`${currency}_${lineOfCredit}`}
          disabled={facility.currency !== currency}
          value={lineOfCredit}
          checked={radioChecked}
          onChange={(event) =>
            handleSubfacilitySelection(event, lineOfCredit, currency, type)
          }
        />
        <label
          className="radio-label"
          htmlFor={`${currency}_${type}_${lineOfCredit}`}
        ></label>
      </div>
    );
  };

  const submitSelection = () => {
    const validSelection = {};
    const currencyValidation = {};
    currencies.forEach((currency) => {
      validSelection[currency] = {
        reverse: {
          checked: false,
          value: "",
        },
        overdraft: {
          checked: false,
          value: "",
        },
      };
      currencyValidation[currency] = {
        validated: false,
      };
    });

    const reverseCreditLines = {};
    currencies.forEach((currency) => {
      reverseCreditLines[currency] = {};
      CREDIT_TYPES.forEach((creditType) => {
        Object.keys(subfacilities[currency]).forEach((credit) => {
          if (subfacilities[currency][credit][creditType]) {
            validSelection[currency][creditType].checked =
              subfacilities[currency][credit][creditType];

            validSelection[currency][creditType].value =
              subfacilities[currency][credit].value;

            if (creditType === "reverse") {
              reverseCreditLines[currency] =
                subfacilities[currency][credit].value;
            }
            return;
          }
        });
      });
    });

    let flatValidation = [];
    let invalidSelection = false;
    currencies.forEach((currency) => {
      const reverseSelection = validSelection[currency]["reverse"].checked;
      const overdraftSelection = validSelection[currency]["overdraft"].checked;
      if (reverseSelection && overdraftSelection) {
        currencyValidation[currency].validated = true;
        return;
      }

      if (
        (!reverseSelection && overdraftSelection) ||
        (reverseSelection && !overdraftSelection)
      ) {
        invalidSelection = true;
      }
    });

    if (invalidSelection) {
      setSelectionError(true);
      return;
    }

    Object.keys(currencyValidation).forEach((currency) => {
      flatValidation.push(currencyValidation[currency].validated);
    });

    if (!flatValidation.some((currentValue) => currentValue)) {
      setSelectionError(true);
      return;
    }

    const newFacilities = [];
    currencies.forEach((currency) => {
      CREDIT_TYPES.forEach((creditType) => {
        facilities.forEach((facility) => {
          if (
            validSelection[currency][creditType].checked &&
            validSelection[currency][creditType].value ===
              facility.lineOfCreditNumber
          ) {
            facility.isOverdraftLine =
              creditType === "overdraft" ? true : false;
            facility.isReverseLine = creditType === "overdraft" ? false : true;

            newFacilities.push(facility);
          }
        });
      });
    });

    handleFacilities(newFacilities, reverseCreditLines, currencies);
  };

  const getData = () => {
    let data = [];
    facilities.map((facility) => {
      data.push({
        ...facility,
        currency: facility.currencyDetail.currencyCode,
        formatApprovedAmount: formatNumber(
          facility.approvedAmount,
          facility.currencyDetail.currencySymbol
        ),
        formatRemainder: formatNumber(
          facility.remainder,
          facility.currencyDetail.currencySymbol
        ),
      });
    });

    return data;
  };

  return (
    <div className="select-facility-modal">
      <SimpleBar forceVisible="y" style={{ maxHeight: 300, width: 980 }}>
        <div className="modal-table">
          <AdminTable headers={getTableColumns()} data={getData()} page={1} />
        </div>
      </SimpleBar>
      <div
        className={`subfacilities-actions-section ${
          selectionError ? "between" : "end"
        }`}
      >
        {selectionError && (
          <ErrorMessage
            error={t(`clients.profile.company.facilities.selectionError`)}
          />
        )}
        <div className="subfacilities-actions">
          <SecondaryButton
            id={`subfacilities-back-btn`}
            className="btnFacility"
            onClick={closeModal}
          >
            {t(`clients.profile.company.facilities.goBack`)}
          </SecondaryButton>
          <SecondaryButton
            id={`subfacilities-save-btn`}
            className="btnFacility"
            onClick={submitSelection}
          >
            {t(`clients.profile.company.facilities.saveChanges`)}
          </SecondaryButton>
        </div>
      </div>
    </div>
  );
};

FacilitiesComponent.propTypes = {
  facilities: PropTypes.array.isRequired,
  selectedFacilities: PropTypes.array.isRequired,
  handleFacilities: PropTypes.func.isRequired,
  closeModal: PropTypes.func.isRequired,
};

export default FacilitiesComponent;
